import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../lib/api';
import { useI18n } from '../i18n/I18nContext';
import { videoEmbed } from './MaintenanceScreen';
import { Button } from './ui/button';
import { CalendarDays, MonitorPlay, Check } from 'lucide-react';

export default function SideColumn() {
  const { t, dateLocale } = useI18n();
  const [events, setEvents] = useState([]);
  const [mine, setMine] = useState({});
  const [videoUrl, setVideoUrl] = useState('');
  const [eventsPageOn, setEventsPageOn] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = () => {
    api.get('/masterclasses').then((r) => {
      const upcoming = (r.data.masterclasses || [])
        .filter((mc) => { try { return new Date(mc.date) >= new Date(); } catch { return true; } })
        .slice(0, 4);
      setEvents(upcoming);
      upcoming.forEach((mc) => {
        api.get(`/masterclasses/${mc.masterclass_id}/my`)
          .then((m) => setMine((prev) => ({ ...prev, [mc.masterclass_id]: m.data.registered })))
          .catch(() => {});
      });
    }).catch(() => {});
    api.get('/settings').then((r) => {
      setVideoUrl((r.data.network_video_url || '').trim());
      setEventsPageOn(r.data.page_masterclass !== '0');
    }).catch(() => {});
  };
  useEffect(load, []); // eslint-disable-line react-hooks/exhaustive-deps

  const register = async (mc) => {
    setBusy(mc.masterclass_id);
    try {
      await api.post(`/masterclasses/${mc.masterclass_id}/register`);
      toast.success(t('masterclass.toastConfirmed'));
      load();
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === 'string' ? err.response.data.detail : t('common.error'));
    } finally {
      setBusy(null);
    }
  };

  const fmtDate = (d) => {
    try { return new Date(d).toLocaleString(dateLocale, { dateStyle: 'medium', timeStyle: 'short' }); }
    catch { return d; }
  };

  return (
    <aside className="space-y-6" data-testid="side-column">
      <div className="bg-nuit-800 border border-nuit-700 rounded-3xl p-5">
        <div className="flex items-center gap-2 mb-4">
          <CalendarDays className="w-5 h-5 text-or-400" strokeWidth={2.5} />
          <h2 className="font-fredoka font-semibold text-lg text-creme" data-testid="side-events-title">{t('side.upcomingTitle')}</h2>
        </div>
        {events.length === 0 ? (
          <p className="text-sm text-brume" data-testid="side-events-empty">{t('side.upcomingEmpty')}</p>
        ) : (
          <div className="space-y-3">
            {events.map((mc) => (
              <div key={mc.masterclass_id} data-testid={`side-event-${mc.masterclass_id}`}
                className="bg-nuit-900 border border-nuit-600 rounded-2xl p-4">
                <p className="font-bold text-creme text-sm">{mc.title}</p>
                <p className="text-xs text-or-300 font-bold mt-1">{fmtDate(mc.date)}</p>
                <p className="text-xs text-brume mt-1">
                  {t(mc.places_left > 1 ? 'masterclass.placeMany' : 'masterclass.placeOne', { n: mc.places_left })}
                </p>
                {mine[mc.masterclass_id] ? (
                  <p className="mt-3 text-xs font-bold text-green-300 flex items-center gap-1.5"
                    data-testid={`side-registered-${mc.masterclass_id}`}>
                    <Check className="w-4 h-4" />{t('masterclass.alreadyRegistered')}
                  </p>
                ) : (
                  <Button onClick={() => register(mc)} disabled={busy === mc.masterclass_id || mc.places_left <= 0}
                    data-testid={`side-register-${mc.masterclass_id}`}
                    className="mt-3 w-full h-10 rounded-full bg-or-400 hover:bg-or-300 text-nuit-950 font-bold text-sm">
                    {t('masterclass.register')}
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
        {eventsPageOn && (
          <Link to="/masterclass" data-testid="side-events-link"
            className="block mt-4 text-center text-xs font-bold text-or-400 hover:underline">
            {t('side.allEvents')}
          </Link>
        )}
      </div>

      {videoUrl && (
        <div className="bg-nuit-800 border border-nuit-700 rounded-3xl p-5" data-testid="side-news-video">
          <div className="flex items-center gap-2 mb-3">
            <MonitorPlay className="w-5 h-5 text-or-400" strokeWidth={2.5} />
            <h2 className="font-fredoka font-semibold text-lg text-creme" data-testid="side-news-title">{t('side.newsTitle')}</h2>
          </div>
          <p className="text-xs text-brume mb-3">{t('side.newsText')}</p>
          {videoEmbed(videoUrl)}
        </div>
      )}
    </aside>
  );
}
